import AlertBanner from "components/AlertBanner";
import { FleetBatteryBanner, useFleetPower } from "components/field/BatteryRunway";
import {
  Activity,
  Archive,
  ArchiveRestore,
  Battery,
  ChevronRight,
  Cpu,
  Droplets,
  MoreVertical,
  RefreshCw,
  Search,
  Settings,
  Signal,
  Thermometer,
  Waves,
  WifiOff,
  Zap,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getSystemOverview, updateDevice } from "services/api";
import {
  STATUS_NEVER,
  STATUS_OFFLINE,
  STATUS_ONLINE,
  STATUS_RECENT,
  classifyDeviceStatus,
  deviceStatusSortOrder,
} from "utils/deviceStatus";

const formatNumber = (value, unit = "", decimals = 1) =>
  value != null ? `${Number(value).toFixed(decimals)}${unit}` : "—";

const formatLastSeen = (value) => {
  if (!value) return "Never";
  const date = new Date(value);
  const minutes = Math.round((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  if (minutes < 1440) return `${Math.round(minutes / 60)}h ago`;
  return date.toLocaleDateString();
};

// Left edge of each card, by connection status.
const STATUS_ACCENT = {
  [STATUS_ONLINE]: "border-l-emerald-500",
  [STATUS_RECENT]: "border-l-amber-400",
  [STATUS_OFFLINE]: "border-l-rose-400",
  [STATUS_NEVER]: "border-l-slate-300",
};

const getBatteryStyle = (status) => {
  switch (status) {
    case "excellent":
      return "bg-emerald-50 text-emerald-700 border-emerald-100";
    case "good":
      return "bg-sky-50 text-sky-700 border-sky-100";
    case "fair":
      return "bg-amber-50 text-amber-700 border-amber-100";
    case "poor":
      return "bg-rose-50 text-rose-700 border-rose-100";
    default:
      return "bg-slate-50 text-slate-600 border-slate-100";
  }
};

const Reading = ({ icon: Icon, label, value, tone }) => (
  <div className="flex min-w-0 items-center gap-1.5" title={label}>
    <Icon className={`h-3.5 w-3.5 shrink-0 ${tone}`} aria-hidden="true" />
    <span className="sr-only">{label}</span>
    <span className="truncate text-xs font-semibold text-slate-700">{value}</span>
  </div>
);

// The card's own menu: archiving hides a device from the fleet, so it sits behind a menu and a
// confirmation instead of next to the link that opens the device.
const CardMenu = ({ device, onArchiveToggle }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (event.type === "keydown" ? event.key === "Escape" : !ref.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
    };
  }, [open]);

  const archive = () => {
    setOpen(false);
    const name = device.name || device.mac_address;
    if (window.confirm(`Archive ${name}? It will be hidden from the Devices list until you unarchive it.`)) {
      onArchiveToggle(device);
    }
  };

  return (
    <div ref={ref} className="relative z-10">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={`More actions for ${device.name || device.mac_address}`}
        aria-haspopup="menu"
        aria-expanded={open}
        className="rounded-full p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
      >
        <MoreVertical className="h-4 w-4" />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 mt-1 w-48 overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
          <Link
            role="menuitem"
            to={`/devices/${encodeURIComponent(device.mac_address)}?tab=config`}
            className="flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            <Settings className="h-4 w-4 text-slate-400" />
            Configure
          </Link>
          <button
            role="menuitem"
            type="button"
            onClick={archive}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-rose-600 hover:bg-rose-50"
          >
            <Archive className="h-4 w-4" />
            Archive device
          </button>
        </div>
      )}
    </div>
  );
};

const DeviceCard = ({ device, onArchiveToggle }) => {
  const status = classifyDeviceStatus(device.last_seen);
  const isArchived = !device.is_active;
  const detailPath = `/devices/${encodeURIComponent(device.mac_address)}`;
  const name = device.name || device.mac_address;

  if (isArchived) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3">
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Archived</p>
          <p className="truncate font-bold text-slate-600">{name}</p>
          <p className="truncate font-mono text-xs text-slate-400">{device.mac_address}</p>
        </div>
        <button
          type="button"
          onClick={() => onArchiveToggle(device)}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-emerald-200 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-50"
        >
          <ArchiveRestore className="h-3.5 w-3.5" />
          Unarchive
        </button>
      </div>
    );
  }

  const hasLevel = device.water_level_cm != null;

  return (
    <div
      className={`group relative flex flex-col rounded-xl border border-l-4 border-slate-200 bg-white shadow-sm transition hover:border-slate-300 hover:shadow-md focus-within:ring-2 focus-within:ring-blue-400 ${STATUS_ACCENT[status.status]}`}
    >
      <div className="flex items-start justify-between gap-3 px-4 pt-3">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-xs">
            <span className={`h-2 w-2 rounded-full ${status.dotClassName}`} />
            <span className={`font-bold ${status.textClassName}`}>{status.label}</span>
            <span className="text-slate-400">· {formatLastSeen(device.last_seen)}</span>
          </div>
          {/* The name's link covers the whole card (after:inset-0); the menu sits above it. */}
          <Link
            to={detailPath}
            className="mt-1 block truncate text-lg font-extrabold text-slate-900 outline-none after:absolute after:inset-0 after:rounded-xl after:content-['']"
          >
            {name}
          </Link>
          <p className="truncate font-mono text-xs text-slate-400">{device.mac_address}</p>
        </div>
        <CardMenu device={device} onArchiveToggle={onArchiveToggle} />
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3 px-4">
        <div className="rounded-lg bg-cyan-50/70 px-3 py-2">
          <p className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-cyan-700">
            <Waves className="h-3.5 w-3.5" /> Water level
          </p>
          <p className={`mt-0.5 text-2xl font-extrabold ${hasLevel ? "text-slate-900" : "text-slate-300"}`}>
            {hasLevel ? Number(device.water_level_cm).toFixed(1) : "—"}
            {hasLevel && <span className="ml-1 text-sm font-semibold text-slate-500">cm</span>}
          </p>
        </div>
        <div className="rounded-lg bg-slate-50 px-3 py-2">
          <p className="flex items-center justify-between gap-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            <span className="flex items-center gap-1">
              <Battery className="h-3.5 w-3.5 text-emerald-600" /> Battery
            </span>
            {device.battery_soc != null && (
              <span className={`rounded-full border px-1.5 text-[11px] font-bold normal-case ${getBatteryStyle(device.battery_status)}`}>
                {Math.round(device.battery_soc)}%
              </span>
            )}
          </p>
          <p className="mt-0.5 text-2xl font-extrabold text-slate-900">
            {device.battery_voltage != null ? Number(device.battery_voltage).toFixed(2) : <span className="text-slate-300">—</span>}
            {device.battery_voltage != null && <span className="ml-1 text-sm font-semibold text-slate-500">V</span>}
          </p>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 px-4 sm:grid-cols-4">
        <Reading icon={Zap} label="Solar" value={formatNumber(device.solar_voltage, " V", 2)} tone="text-yellow-500" />
        <Reading icon={Thermometer} label="Temperature" value={formatNumber(device.temperature, " °C", 1)} tone="text-orange-500" />
        <Reading icon={Droplets} label="Humidity" value={formatNumber(device.humidity, "%", 0)} tone="text-blue-500" />
        <Reading icon={Signal} label="Readings in the last 24 h" value={`${device.readings_24h ?? "—"} / 24h`} tone="text-violet-500" />
      </div>

      <div className="mt-3 flex items-center justify-between gap-2 border-t border-slate-100 px-4 py-2 text-xs text-slate-500">
        <span className="truncate">Firmware {device.firmware_version || "unknown"}</span>
        <span className="flex shrink-0 items-center gap-0.5 font-semibold text-blue-600">
          View details
          <ChevronRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
        </span>
      </div>
    </div>
  );
};

const SORT_OPTIONS = [
  { key: "name",   label: "Name A→Z" },
  { key: "seen",   label: "Last Seen" },
  { key: "status", label: "Status" },
];

const statusOrder = (device) => deviceStatusSortOrder(device.last_seen);

// The summary tiles double as filters. "Online" is seen within the hour (online or recent), the same
// window as the backend's online_1h count, so a tile's number matches the cards it shows.
const isSeenWithinHour = (device) =>
  [STATUS_ONLINE, STATUS_RECENT].includes(classifyDeviceStatus(device.last_seen).status);

export default function DevicesOverviewPage() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("name");
  const [showArchived, setShowArchived] = useState(false);
  const [filter, setFilter] = useState("all");
  // Field nodes with an active battery warning (battery runway, 5-minute poll).
  const batteryWarnings = useFleetPower();

  const fetchOverview = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError(null);
    try {
      setOverview(await getSystemOverview(showArchived));
    } catch (err) {
      setError(err.message);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    fetchOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchived]);

  const handleArchiveToggle = async (device) => {
    try {
      await updateDevice(device.mac_address, { is_active: !device.is_active });
      fetchOverview(false);
    } catch (err) {
      setError(err.message);
    }
  };

  const devices = useMemo(() => {
    const raw = overview?.devices_list || [];
    const lowBattery = new Set((overview?.alerts?.low_battery_devices || []).map((d) => d.device));
    const matchesFilter = {
      all: () => true,
      online: isSeenWithinHour,
      offline: (d) => !isSeenWithinHour(d),
      lowBattery: (d) => lowBattery.has(d.mac_address),
    }[filter];
    const q = search.trim().toLowerCase();
    const filtered = raw.filter(
      (d) =>
        matchesFilter(d) &&
        (!q || (d.name || "").toLowerCase().includes(q) || d.mac_address.toLowerCase().includes(q))
    );

    return [...filtered].sort((a, b) => {
      if (sort === "name")
        return (a.name || a.mac_address).localeCompare(b.name || b.mac_address);
      if (sort === "seen")
        return (new Date(b.last_seen || 0)) - (new Date(a.last_seen || 0));
      if (sort === "status")
        return statusOrder(a) - statusOrder(b);
      return 0;
    });
  }, [overview, search, sort, filter]);

  return (
    <div className="mx-auto max-w-7xl space-y-5 px-4 py-5 sm:px-6 lg:px-8">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <h1 className="font-headline text-3xl font-extrabold tracking-tight text-on-surface">
            Devices
          </h1>
          <p className="mt-1 text-sm text-on-surface-variant">
            Fleet status, battery, water and environment readings
          </p>
        </div>
        <button
          onClick={() => fetchOverview(false)}
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-on-primary transition-colors hover:bg-primary-container disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      <AlertBanner type="error" message={error} onDismiss={() => setError(null)} />
      <FleetBatteryBanner nodes={batteryWarnings} />

      {overview && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {[
            { key: "all", label: "All devices", value: overview.devices.total, icon: Cpu, tone: "text-blue-600", ring: "ring-blue-500" },
            { key: "online", label: "Online (1h)", value: overview.devices.online_1h, icon: Activity, tone: "text-emerald-600", ring: "ring-emerald-500" },
            { key: "offline", label: "Offline", value: overview.devices.offline, icon: WifiOff, tone: "text-rose-600", ring: "ring-rose-500" },
            { key: "lowBattery", label: "Low battery", value: overview.alerts.low_battery_count, icon: Battery, tone: "text-amber-600", ring: "ring-amber-500" },
          ].map(({ key, label, value, icon: Icon, tone, ring }) => {
            const active = filter === key;
            return (
              <button
                key={key}
                type="button"
                aria-pressed={active}
                onClick={() => setFilter(active ? "all" : key)}
                className={`rounded-xl border bg-white p-3 text-left shadow-sm transition hover:border-slate-300 ${
                  active ? `border-transparent ring-2 ${ring}` : "border-slate-200"
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-500">{label}</p>
                  <Icon className={`h-5 w-5 ${tone}`} />
                </div>
                <p className="mt-1 text-2xl font-extrabold text-slate-900">{value}</p>
              </button>
            );
          })}
        </div>
      )}

      {/* Search + sort toolbar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Search by name or MAC…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-800 placeholder-slate-400 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
        </div>
        <div className="flex gap-1.5 shrink-0 flex-wrap">
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              onClick={() => setSort(opt.key)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                sort === opt.key
                  ? "bg-blue-600 text-white shadow-sm"
                  : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
            >
              {opt.label}
            </button>
          ))}
          <button
            onClick={() => setShowArchived((v) => !v)}
            className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
              showArchived
                ? "bg-slate-700 text-white shadow-sm"
                : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
            }`}
          >
            <Archive className="h-3 w-3" />
            {showArchived ? "Hide archived" : "Show archived"}
          </button>
        </div>
      </div>

      {loading && !overview ? (
        <div className="rounded-lg border border-slate-200 bg-white p-10 text-center">
          <RefreshCw className="mx-auto h-8 w-8 animate-spin text-blue-500" />
          <p className="mt-3 text-sm font-semibold text-slate-600">Loading devices...</p>
        </div>
      ) : (
        <div className="grid gap-3 xl:grid-cols-2">
          {devices.map((device) => (
            <DeviceCard key={device.mac_address} device={device} onArchiveToggle={handleArchiveToggle} />
          ))}
          {overview && devices.length === 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500 xl:col-span-2">
              No devices match.
              {(filter !== "all" || search) && (
                <button
                  type="button"
                  onClick={() => {
                    setFilter("all");
                    setSearch("");
                  }}
                  className="ml-2 font-semibold text-blue-600 hover:underline"
                >
                  Clear filters
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
